import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icon, ButtonIcon, TextInput, ButtonGroup, Switcher, Button, Checkbox, FileUploadV2, Modal, Notification, Dropdown, TextArea, Search, Radio, Breadcrumb } from "pepsico-ds";
import { useCallback, useEffect, useRef, useState } from "react";
import helpOutlineIcon from "../../assets/guest_invitation/help_outline.svg"
import "./ir-incident-response.css"
import irService from "./ir.service";
import { toast, ToastContainer } from 'react-toastify';
import { useLoading } from "../../loader";
import axios from "axios";

const IrIncidentResponse = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { REACT_APP_API_ENDPOINT } = process.env;
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { showLoader, hideLoader } = useLoading();
    const [loader, setLoader] = useState(false);
    // const [showModal, setShowModal] = useState(false);
    const [copyButtonText, setCopyButtonText] = useState(t('nonArExtension.copy'));
    const [requestData, setRequestData] = useState(null);
    const [description, setDescription] = useState("");
    const [incidentNumber, setIncidentNumber] = useState("");
    const incidentRef = useRef(null);
    const [accountName, setAccountName] = useState("");
    const [showNa, setShowNa] = useState(false);
    const [incidentNumError, setIncidentNumError] = useState(false);
    const [applicationName, setApplicationName] = useState("");
    const [applicationOwner, setApplicationOwner] = useState("");
    const [disableAppSearch, setDisableAppSearch] = useState(false);
    const [isAccountValid, setIsAccountValid] = useState(false);
    const [environment, setEnvironment] = useState([])
    const [environments, setEnvironments] = useState([
        {
            id: 'CORPTST', displayText: t("ir.corptst")
        },
        {
            id: 'CWWTST', displayText: t("ir.cwwtst")
        },
        {
            id: 'CORPPVT', displayText: t("ir.corppvt")
        },
        {
            id: 'CWWPVT', displayText: t("ir.cwwpvt")
        }
    ]);

    function useDebouncedCallback(callback, delay = 1000) {
        const timerRef = useRef();

        const debounced = useCallback((...args) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        }, [callback, delay]);

        useEffect(() => {
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            };
        }, []);

        return debounced;
    }

    const searchAccountDetails = async (text) => {
        try {
            showLoader();
            setDisableAppSearch(true);
            setLoader(true);
            const payload = {
                typeOfAccount: "ACTIVE_DIRECTORY", 
                serviceAccountName: accountName, 
                environment: environment?.[0]?.id 
            }
            const response = await irService.accountSearch(payload);
            if(response?.status === "Failed"){
                hideLoader();
                setDisableAppSearch(false);
                setIsAccountValid(false);
                setLoader(false);
                toast.error(response?.errorMessage,"error");
                return
            }
            setApplicationName(response?.applicationName);
            setApplicationOwner(response?.applicationOwner);
            setIsAccountValid(true);
            setShowNa(true)
            hideLoader();
            setDisableAppSearch(false);
            setLoader(false);
        } catch (error) {
            toast.error(error.message)
            console.log('error', error);
            hideLoader();
            setLoader(false);
        } finally {
            hideLoader();
            setDisableAppSearch(false);
            setLoader(false);
        }
    };

    const debouncedSearch = useDebouncedCallback((q) => {
        searchAccountDetails();
    }, 500);

    useEffect(()=>{
        if(accountName.length>3 && environment?.[0]?.id){
            setApplicationName("");
            setApplicationOwner("");
            setIsAccountValid(false);
            setShowNa(false);
            debouncedSearch();
        }else if(!accountName){
            setDescription("");
            setIncidentNumber("");
            setIsAccountValid(false);
            setIncidentNumError(false);
            setApplicationName("");
            setApplicationOwner("");
            if(environment.length)setEnvironment([]);
            setShowNa(false);
        }
    },[accountName, environment])

    // const [applications, setApplications] = useState([]);
    // const [selectedApp, setSelectedApp] = useState('')
    // const [searchedAppQuery, setSearchedAppQuery] = useState("");
    
    const resetFields = () => {
        setAccountName("");
        setDescription("");
        setIncidentNumber("");
        setIsAccountValid(false);
        setIncidentNumError(false);
        setApplicationName("");
        setApplicationOwner("");
        setEnvironment([]);
        setShowNa(false);
    }

    const handleSubmit = async () => {
        showLoader();
        try{
            showLoader();
            setDisableAppSearch(true);
            setLoader(true);
            const payload = {
                typeOfAccount: "ACTIVE_DIRECTORY", 
                serviceAccountName: accountName, 
                environment: environment?.[0]?.id,
                incidentNumber: incidentNumber, 
                applicationName: applicationName, 
                applicationOwner: applicationOwner, 
                businessJustification : description
            }
            const response = await irService.accountSuspend(payload);
            if (response?.responseStatus === "Failed" || response?.status === "Failed") {
                hideLoader();
                setLoader(false);
                // setShowModal(true);
                toast.error(response?.errorMessage ,"error");
                if(response?.errorMessage?.toLowerCase?.()?.includes?.("incident number")){
                    setIncidentNumber("");
                    incidentRef?.current?.focus?.();
                    incidentRef?.current?.scrollIntoView?.({
                        behavior: "smooth",
                        block: "center",
                        inline: "nearest"
                    });
                }
            }
            else {
                hideLoader();
                setLoader(false);
                setRequestData(response);
                resetFields();
                toast.success(`${response?.message} ${t("ir.with_requestID")} ${response?.requestId}` ,"success");
            }
        }catch (error) {
            hideLoader();
            setLoader(false);
            toast.error(error,"error");
        }finally{
            hideLoader();
            setLoader(false);
        }
    }

    const disableSubmit = () => {
        return !(accountName.length>3 && environment?.[0]?.id && incidentNumber.length>3 && description && isAccountValid && !incidentNumError)
    }

    // const closeModal = () => {
    //     setShowModal(false);
    // }

    const onClickCopynumber = () => {
        if (requestData?.requestNumber) {
            navigator.clipboard.writeText(requestData?.requestNumber?.toString?.()).then(() => {
                setCopyButtonText(t('nonArExtension.copied'));
            }).catch(err => {
                console.error("Failed to copy text: ", err);
            });
        }
    };

    const onViewRequest = () => {
        const statePayload = { type: "requestNumber", value: requestData?.requestNumber };
        navigate("/audit-requests", { state: statePayload });
    }

    // const renderConfirmModal = () => {
    //     return (
    //         <div className="confirm-modal">
    //             <Modal
    //                 showModal={showModal}
    //                 className="ir-success-modal"
    //                 onCloseModal={closeModal}
    //                 closeOnOutsideClick={false}
    //                 notificationsProps={{
    //                     secondaryActionLabel: (
    //                         <>
    //                             <Icon
    //                                 alt="alt value"
    //                                 icon={copyButtonText == t('nonArExtension.copied') ? "check" : "content_copy"}
    //                                 size="medium"
    //                                 style={{ marginRight: '6px', display: 'unset' }}
    //                             />

    //                             {copyButtonText}
    //                         </>
    //                     ),
    //                     secondaryActionOnClick: onClickCopynumber,
    //                     text: `${requestData?.requestNumber}`,
    //                     title: t('nonArExtension.request_number_title'),
    //                     type: 'info'
    //                 }}
    //                 showAuxiliaryButton={false}
    //                 showLink={false}
    //                 showPrimaryButton={true}
    //                 primaryButtonProps={{
    //                     onClick: onViewRequest,
    //                     size: 'medium',
    //                     text: t('homePage.view_request'),
    //                     variant: 'primary'
    //                 }}
    //                 showSecondaryButton={false}
    //                 showSlot
    //                 showTertiaryButton={true}
    //                 tertiaryButtonProps={{
    //                     onClick: closeModal,
    //                     size: 'medium',
    //                     text: t('nonArExtension.back'),
    //                     variant: 'tertiary'
    //                 }}
    //                 showCloseButton={true}
    //                 style={{}}
    //                 text={<>
    //                     <div style={{ marginTop: '10px' }}>
    //                         <p className="modal-para-text">{t('entraGuest.entra_request_confirmation_text')}
    //                         </p>
    //                     </div>
    //                 </>}
    //                 title={t('entraGuest.entra_request_confirmation_title')}
    //                 type="fluid"
    //                 viewPort={`${isMobile ? "mobile" : "desktop"}`}
    //             >
    //                 <p style={{ fontSize: '16px' }} className="modal-para-text">{`${t('entraGuest.see_all_your_requests_in_the')} `} <span className="view-request-txt">{`${t('entraGuest.view_request')} `}</span> {t('entraGuest.section_below')}
    //                 </p>
    //             </Modal>
    //         </div>
    //     )
    // }

    return (
        <>
            <div className="ir-main-container">
                <Breadcrumb
                    homeIcon={{
                        icon: "home",
                        onClick:()=>{
                        navigate("/home")
                        }
                    }}
                    breadcrumbs={[
                        {
                            label: t("menu.home"),
                        },
                        {
                            label: t("ir.suspend_service_account"),
                        },
                    ]}
                    className="mx-3 non-clickable-breadcrumbs"
                    onClick={function Xs() { }}
                    useAnchor
                    style={{ marginBottom: "1rem !important" }}
                />
                <div
                    className={isMobile ? "row mt-2" : "container mt-2"}
                    style={{
                        background: "none",
                        boxShadow: "none",
                        justifyContent: isMobile ? "center" : "",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "0px" : "15px",
                            flexWrap: "wrap",
                            flex: 1,
                        }}
                    >
                        <div className={isMobile ? "row mt-2" : ""} style={{ justifyContent: isMobile ? "center" : "" }}>
                            <div className={`${isMobile ? 'pl-4 pr-4 ml-4 mr-4' : 'd-flex mb-0'}  align-items-start ad-main-heading-container`} style={{ gap: "12px" }}>
                                {/* <ButtonIcon
                                    className=""
                                    color="Interaction/Active"
                                    icon="error"
                                    label="Label"
                                    onClick={() => {}}
                                    size="medium"
                                    style={{}}
                                    type="default"
                                /> */}
                                <div className="d-flex flex-column">
                                    <div className="ad-main-heading">{t("ir.suspend_service_account")}</div>
                                    <div className="ad-main-desc">{t("ir.suspend_service_account_desc")}</div>
                                </div>
                            </div>
                            <div
                                className="card-layout reg-card mb-3 mb-md-0 active-directory-card-container ir-container"
                                style={{
                                    flex: isMobile ? "unset" : "0 0 calc(66.666% - 7.5px)",
                                    width: isMobile ? "100%" : "auto",
                                    flexDirection: isMobile ? "column" : "row",
                                    gap: "1rem"
                                }}
                            >
                                <div className="w-100">
                                    <div className="card card-content align-items-center">
                                        <div className="bulk-upload-section w-50">
                                            <Radio
                                                className="mb-3"
                                                feedbackType="info"
                                                items={[
                                                    t("ir.active_directory")
                                                ]}
                                                label={t("ir.type_of_account")}
                                                orientation="vertical"
                                                reference="radio button"
                                                required={true}
                                                showText
                                                size="small"
                                                style={{}}
                                                selectedItemIndex={0}
                                                // disabled={true}
                                            />
                                            {/* <div className="form-field mb-3">
                                                <div className="" style={{ display: 'flex', justifyContent: "space-between" }}>
                                                    <label className="safe-name" style={{ display: "flex", alignItems: "center" }}>
                                                        {t("ir.service_account_name")}
                                                    </label>
                                                    <label className="required-text $font-xsmall-regular" style={{ color: "var(--color-text-secondary)" }}>*Required</label>
                                                </div>
                                                <Search
                                                    className="mb-3"
                                                    isAutoComplete
                                                    isClearable={true}
                                                    isDisabled={disableAppSearch}
                                                    required
                                                    isRequired
                                                    maxLength={20}
                                                    onClear={() => {
                                                        setSelectedApp(null);
                                                    }}
                                                    onSelectOption={(val) => {
                                                        const sel = Array.isArray(val) ? val[0] : val;
                                                        setSelectedApp(sel);
                                                    }}
                                                    onSearch={(val) => searchApplications(val)}
                                                    onUpdate={(text) => {
                                                        debouncedSearch(text);
                                                        setSearchedAppQuery(text.trimStart())
                                                    }}
                                                    placeholderText={t("ir.service_account_name_placeholder")}
                                                    size="medium"
                                                    autoComplete="off"
                                                    suggestions={applications}
                                                    selectedValue={selectedApp}
                                                    limitSuggestions={applications.length}
                                                    searchTerm={searchedAppQuery}
                                                />
                                            </div> */}
                                            <div className="mb-3">
                                                <TextInput
                                                    name="accountName"
                                                    type="text"
                                                    value={accountName}
                                                    label={t("ir.service_account_name")}
                                                    placeholderText={t("ir.service_account_name_placeholder")}
                                                    onChange={(e) => {
                                                        setAccountName(e.target.value);
                                                    }}
                                                    required={true}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <Dropdown
                                                    childList={environments}
                                                    label={t("active_directory.domain")}
                                                    placeholder={t("RequestGPIDForNonEmployee.select_btn_label")}
                                                    menuPosition="bottom-right"
                                                    isClearable={true}
                                                    selection="single"
                                                    size="medium"
                                                    setSelectedValue={(selected) => setEnvironment(selected)}
                                                    selectedValue={environment}
                                                    onKeyDown={(e) => e.preventDefault()}
                                                    showLabelIcon={false}
                                                    isRequired
                                                    disabled={!accountName.length}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextInput
                                                    name="incidentNumber"
                                                    type="text"
                                                    ref={incidentRef}
                                                    value={incidentNumber}
                                                    label={t("ir.incident_number")}
                                                    placeholderText={t("ir.incident_number_placeholder")}
                                                    error={incidentNumError && t("idx.alphanumeric_error")}
                                                    onChange={(e) => {
                                                        if(e.target.value && /^[A-Za-z0-9]+$/.test(e.target.value)){
                                                            setIncidentNumber(e.target.value?.toUpperCase());
                                                            setIncidentNumError(false);
                                                        }else{
                                                            if(e.target.value && !/^[A-Za-z0-9]+$/.test(e.target.value)) {
                                                                setIncidentNumError(true);
                                                                setIncidentNumber(e.target.value?.toUpperCase());
                                                            }
                                                            else setIncidentNumError(false);
                                                        }
                                                    }}
                                                    required={true}
                                                    maxLength={15}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextInput
                                                    name="applicationName"
                                                    type="text"
                                                    value={applicationName || (showNa ? t("entraGuest.na") : applicationName)}
                                                    label={t("ir.application_name")}
                                                    placeholderText={t("ir.application_name")}
                                                    disabled={true}
                                                    onChange={(e) => {
                                                        setApplicationName(e.target.value);
                                                    }}
                                                />
                                            </div>
                                            <div className="mb-3">
                                                <TextInput
                                                    name="applicationOwner"
                                                    type="text"
                                                    value={applicationOwner || (showNa ? t("entraGuest.na") : applicationOwner)}
                                                    label={t("ir.application_owner")}
                                                    placeholderText={t("ir.application_owner")}
                                                    disabled={true}
                                                    onChange={(e) => {
                                                        setApplicationOwner(e.target.value);
                                                    }}
                                                />
                                            </div>
                                            <TextArea
                                                title={t("mypam.business_justification")}
                                                label={t("mypam.business_justification")}
                                                placeholder={t("idx.type_here")}
                                                rows={3}
                                                style={{ width: "100%"}}
                                                onChange={(e) => setDescription(e.target.value)}
                                                value={description}
                                                showToolbar={false}
                                                required={true}
                                                showCharacterCount={true}
                                                maxLength={100}
                                                showLabelIcon={false}
                                                disableResize={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="ir-footer mt-3">
                <div className="row footer-row-with-footer-buttons">
                    <div className="footer-buttons">
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0 px-md-0 justify-content-center">
                            <Button
                                className="search-user-cancel-button mr-md-2 my-0"
                                type="button"
                                size="medium"
                                text={t("RequestGPIDForNonEmployee.cancel_btn_label")}
                                variant="tertiary"
                                onClick={() => { navigate(-1) }}
                            />
                        </div>
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0">
                            <Button
                                className="search-user-select-button request-button my-0 px-3 justify-content-center"
                                type="button"
                                size="medium"
                                text={t("nonArExtension.submit")}
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={disableSubmit()}
                            />
                        </div>
                    </div>
                </div>
                <ToastContainer
                    position="bottom-center"
                />
                {/* {showModal && renderConfirmModal()} */}
            </div>
        </>
    )
}

export default IrIncidentResponse;